import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { EditorView } from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import type { Extension } from "@codemirror/state";

/**
 * Temas do editor. A interface do Kompil é monocromática (seção 21); o código, não precisa ser:
 * por padrão a sintaxe é colorida (tags, atributos, palavras-chave e tipos se reconhecem de
 * relance) e os problemas ganham sublinhado ondulado na cor da gravidade. O tema monocromático
 * continua disponível nas preferências (ADR 0005).
 */
function palette(dark: boolean) {
  return dark
    ? {
        bg: "#101010",
        gutter: "#101010",
        fg: "#f5f5f5",
        strong: "#ffffff",
        muted: "#a3a3a3",
        dim: "#5c5c5c",
        line: "#161616",
        // Seleção branca translúcida (o texto continua legível por cima); mais forte com foco. O
        // azul do VS Code vem do tema dele, quando aplicado.
        sel: "rgba(255, 255, 255, 0.12)",
        selFocus: "rgba(255, 255, 255, 0.22)",
        border: "#1f1f1f",
        match: "#333333",
      }
    : {
        bg: "#ffffff",
        gutter: "#ffffff",
        fg: "#1a1a1a",
        strong: "#000000",
        muted: "#5a5a5a",
        dim: "#9a9a9a",
        line: "#f6f6f6",
        // No claro, o "branco" do Kompil é o contraste: cinza translúcido (sem cor).
        sel: "rgba(0, 0, 0, 0.07)",
        selFocus: "rgba(0, 0, 0, 0.14)",
        border: "#ececec",
        match: "#d4d8dc",
      };
}

type Palette = ReturnType<typeof palette>;

export interface Signals {
  error: string;
  warning: string;
  info: string;
  hint: string;
}

/** Problemas nos temas coloridos (plugins): vermelho = erro, âmbar = aviso, violeta = IA. */
export function signalColors(dark: boolean): Signals {
  return dark
    ? { error: "#f87171", warning: "#fbbf24", info: "#60a5fa", hint: "#c084fc" }
    : { error: "#dc2626", warning: "#b45309", info: "#2563eb", hint: "#7c3aed" };
}

/** Fundo translúcido de uma cor #rrggbb. */
const tint = (hex: string, alpha: number) =>
  `${hex}${Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0")}`;

/**
 * Problemas no tema padrão (preto e branco): a gravidade fica na forma e na intensidade — erro
 * em ondulado forte, aviso e informação em cinza; nenhuma cor.
 */
function monoSignals(c: Palette): Signals {
  return { error: c.strong, warning: c.muted, info: c.muted, hint: c.muted };
}

function viewTheme(c: Palette, dark: boolean, s: Signals): Extension {
  const squiggle = (color: string, style: "wavy" | "dotted") => ({
    backgroundImage: "none",
    paddingBottom: "0",
    textDecoration: `underline ${style} ${color}`,
    textDecorationSkipInk: "none",
    textDecorationThickness: style === "wavy" ? "1.5px" : "2px",
    textUnderlineOffset: "3px",
  });
  const marker = (color: string) => ({
    content: "normal",
    width: "7px",
    height: "7px",
    margin: "0 auto",
    borderRadius: "999px",
    backgroundColor: color,
    boxShadow: `0 0 0 3px ${tint(color, 0.18)}`,
  });
  return EditorView.theme(
    {
      // Tamanho escolhido pela pessoa (Exibir › letra do editor, Ctrl + roda do mouse).
      "&": { color: c.fg, backgroundColor: c.bg, height: "100%", fontSize: "var(--editor-font-size, 14px)" },
      ".cm-scroller": { fontFamily: "var(--font-mono)", lineHeight: "1.7" },
      ".cm-content": { caretColor: c.strong, padding: "10px 0 40px" },
      // Recuo: o código respira longe da numeração (sem linha divisória entre os dois).
      ".cm-line": { padding: "0 24px 0 14px" },
      ".cm-cursor, .cm-dropCursor": { borderLeftColor: c.strong, borderLeftWidth: "2px" },
      // Seleção em faixa cheia por linha (como no VS Code), cantos levemente arredondados; mais
      // forte com o editor em foco. A cor é a do tema (branca no padrão, a do tema do VS Code nele).
      ".cm-selectionBackground": {
        backgroundColor: `${c.sel} !important`,
        borderRadius: "3px",
      },
      "&.cm-focused .cm-selectionBackground": {
        backgroundColor: `${c.selFocus} !important`,
      },
      // A seleção nativa fica invisível: só a camada acima é desenhada (translúcida, não pode somar).
      ".cm-content ::selection, .cm-line::selection": { backgroundColor: "transparent !important" },
      // Linha do cursor translúcida: opaca, ela cobria a seleção (que fica numa camada atrás do
      // texto) e a palavra selecionada não aparecia.
      ".cm-activeLine": { backgroundColor: /^#[0-9a-f]{6}$/i.test(c.line) ? tint(c.line, 0.55) : c.line },
      ".cm-gutters": { backgroundColor: c.gutter, color: c.dim, border: "none" },
      ".cm-activeLineGutter": { backgroundColor: c.line, color: c.fg },
      ".cm-lineNumbers .cm-gutterElement": { padding: "0 6px 0 20px", minWidth: "44px" },
      // Setas de dobrar o código só aparecem com o mouse na margem (como no VS Code).
      ".cm-foldGutter .cm-gutterElement": { opacity: "0", transition: "opacity 120ms" },
      ".cm-gutters:hover .cm-foldGutter .cm-gutterElement": { opacity: "1" },
      // Cursores das outras pessoas: barra na cor dela, deslizando, com o nome numa etiqueta.
      ".cm-remote-caret": {
        position: "absolute",
        left: "0",
        top: "0",
        width: "2px",
        marginLeft: "-1px",
        borderRadius: "1px",
        backgroundColor: "var(--caret)",
        pointerEvents: "none",
        transition: "transform 110ms cubic-bezier(0.2, 0.8, 0.2, 1), height 110ms",
        willChange: "transform",
      },
      ".cm-remote-caret-label": {
        position: "absolute",
        bottom: "100%",
        left: "-1px",
        marginBottom: "1px",
        padding: "1px 6px",
        borderRadius: "6px 6px 6px 2px",
        backgroundColor: "var(--caret)",
        color: "var(--caret-text)",
        fontFamily: "var(--font-sans)",
        fontSize: "11px",
        fontWeight: "600",
        lineHeight: "15px",
        whiteSpace: "nowrap",
        animation: "kompil-caret-label 4s ease forwards",
      },
      ".cm-remote-caret-top .cm-remote-caret-label": {
        bottom: "auto",
        top: "100%",
        marginTop: "1px",
        borderRadius: "2px 6px 6px 6px",
      },
      ".cm-ySelection": { borderRadius: "2px" },
      ".cm-foldPlaceholder": {
        backgroundColor: "transparent",
        border: `1px solid ${c.match}`,
        color: c.muted,
      },
      ".cm-matchingBracket, .cm-nonmatchingBracket": { backgroundColor: c.match, outline: "none" },
      ".cm-nonmatchingBracket": { color: `${s.error} !important` },
      // Tag correspondente (abertura ↔ fechamento) sob o cursor.
      ".cm-matchingTag": {
        backgroundColor: c.match,
        borderRadius: "2px",
        boxShadow: `0 1px 0 0 ${c.muted}`,
      },
      ".cm-searchMatch": { backgroundColor: c.match, outline: `1px solid ${c.muted}` },
      ".cm-searchMatch.cm-searchMatch-selected": { backgroundColor: c.muted, color: c.bg },
      ".cm-selectionMatch": { backgroundColor: c.sel },
      ".cm-tooltip": {
        backgroundColor: dark ? "#151515" : "#ffffff",
        border: `1px solid ${c.match}`,
        borderRadius: "6px",
        color: c.fg,
        overflow: "hidden",
      },
      ".cm-tooltip-autocomplete ul": { fontFamily: "var(--font-mono)", fontSize: "12.5px", padding: "4px" },
      ".cm-tooltip-autocomplete ul li": { padding: "3px 8px", borderRadius: "5px", lineHeight: "1.5" },
      ".cm-tooltip-autocomplete ul li[aria-selected]": { backgroundColor: c.sel, color: c.strong },
      ".cm-completionDetail": {
        fontFamily: "var(--font-sans)",
        fontStyle: "normal",
        color: c.dim,
        marginLeft: "1em",
      },
      ".cm-completionMatchedText": { textDecoration: "none", fontWeight: "700", color: c.strong },
      ".cm-tooltip.cm-completionInfo": { padding: "0", maxWidth: "420px" },
      ".cm-emmet-preview": {
        margin: "0",
        padding: "8px 10px",
        maxHeight: "260px",
        overflow: "auto",
        fontFamily: "var(--font-mono)",
        fontSize: "11.5px",
        lineHeight: "1.55",
        color: c.muted,
        tabSize: "2",
      },
      ".cm-panels": {
        backgroundColor: dark ? "#101010" : "#fafafa",
        color: c.fg,
        borderTop: `1px solid ${c.border}`,
      },
      ".cm-panel input, .cm-panel button": { fontFamily: "var(--font-sans)" },
      // O cursor do y-codemirror (que recriava o elemento a cada tecla) fica escondido: a camada
      // própria (remote-cursors) desenha o cursor e o nome deslizando.
      ".cm-ySelectionCaret": { display: "none !important" },
      // Sugestões abertas: sublinhado tracejado monocromático.
      ".cm-suggestion": {
        textDecoration: `underline dashed ${c.muted}`,
        textUnderlineOffset: "3px",
        backgroundColor: c.line,
      },
      ".cm-ghost": { color: c.dim, fontStyle: "italic" },
      // Comentários na linha: margem com o ícone da conversa ou "+" ao passar o mouse.
      ".cm-comment-gutter": { width: "18px" },
      ".cm-comment-gutter .cm-gutterElement": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      },
      // Bolha no fim da linha comentada (inicial de quem comentou + mensagens).
      ".cm-comment-bubble": {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        height: "18px",
        marginLeft: "12px",
        padding: "0 7px 0 2px",
        verticalAlign: "1px",
        borderRadius: "999px",
        border: `1px solid ${c.border}`,
        backgroundColor: c.line,
        color: c.muted,
        fontFamily: "var(--font-sans)",
        fontSize: "11px",
        fontWeight: "600",
        lineHeight: "16px",
        cursor: "pointer",
        transition: "color 120ms, border-color 120ms",
      },
      ".cm-comment-bubble:hover": { color: c.fg, borderColor: c.match },
      ".cm-comment-bubble-avatar": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "14px",
        height: "14px",
        borderRadius: "999px",
        backgroundColor: "var(--c)",
        color: "#0b0b0b",
        fontSize: "9px",
      },
      ".cm-comment-bubble-resolved": { opacity: "0.5" },
      ".cm-comment-card": { cursor: "auto" },
      ".cm-comment-add": {
        width: "14px",
        height: "14px",
        lineHeight: "13px",
        textAlign: "center",
        borderRadius: "3px",
        fontFamily: "var(--font-sans)",
        fontWeight: "600",
        color: c.bg,
        backgroundColor: c.muted,
        opacity: "0",
      },
      ".cm-comment-gutter .cm-gutterElement:hover .cm-comment-add": { opacity: "1" },

      // ───── Problemas no código ─────
      ".cm-lintRange-error": squiggle(s.error, "wavy"),
      ".cm-lintRange-warning": squiggle(s.warning, "wavy"),
      ".cm-lintRange-info": squiggle(s.info, "wavy"),
      ".cm-lintRange-hint": squiggle(s.hint, "dotted"),
      ".cm-lintRange-active": { backgroundColor: c.sel },
      ".cm-unreachable": { opacity: "0.5" },
      ".cm-improvement": { backgroundColor: tint(s.hint, dark ? 0.1 : 0.07), borderRadius: "2px" },
      ".cm-gutter-lint": { width: "1.2em" },
      ".cm-gutter-lint .cm-gutterElement": { padding: "0", display: "flex", alignItems: "center" },
      ".cm-lint-marker-error": marker(s.error),
      ".cm-lint-marker-warning": marker(s.warning),
      ".cm-lint-marker-info": marker(s.info),
      ".cm-lint-marker-hint": marker(s.hint),
      ".cm-tooltip-lint": { padding: "0", fontFamily: "var(--font-sans)", fontSize: "12.5px" },
      ".cm-diagnostic": {
        padding: "7px 12px 7px 10px",
        marginLeft: "0",
        borderLeft: "3px solid transparent",
        maxWidth: "560px",
        whiteSpace: "pre-wrap",
      },
      ".cm-diagnostic + .cm-diagnostic": { borderTop: `1px solid ${c.border}` },
      ".cm-diagnostic-error": { borderLeftColor: s.error },
      ".cm-diagnostic-warning": { borderLeftColor: s.warning },
      ".cm-diagnostic-info": { borderLeftColor: s.info },
      ".cm-diagnostic-hint": { borderLeftColor: s.hint },
      ".cm-diagnosticSource": {
        fontFamily: "var(--font-mono)",
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        opacity: "0.6",
        marginTop: "4px",
      },
      ".cm-diagnosticAction": {
        font: "inherit",
        fontSize: "11.5px",
        fontWeight: "600",
        backgroundColor: c.strong,
        color: c.bg,
        border: "none",
        borderRadius: "5px",
        padding: "2px 8px",
        marginLeft: "0",
        marginRight: "6px",
        marginTop: "6px",
        cursor: "pointer",
      },
      ".cm-diagnosticAction.cm-action-secondary": {
        backgroundColor: "transparent",
        color: c.fg,
        boxShadow: `inset 0 0 0 1px ${c.match}`,
      },
      ".cm-panel.cm-panel-lint ul": { fontFamily: "var(--font-sans)", fontSize: "12.5px" },
      ".cm-panel.cm-panel-lint ul [aria-selected]": { backgroundColor: c.sel },
      ".cm-panel.cm-panel-lint ul:focus [aria-selected]": { backgroundColor: c.selFocus, color: c.strong },
      // Mensagem do problema no fim da linha (estilo "error lens").
      ".cm-inline-diagnostic": {
        fontFamily: "var(--font-sans)",
        fontSize: "11.5px",
        fontStyle: "italic",
        marginLeft: "2.5ch",
        padding: "0 6px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
        pointerEvents: "none",
      },
      ".cm-inline-diagnostic-error": { color: s.error, backgroundColor: tint(s.error, 0.1) },
      ".cm-inline-diagnostic-warning": { color: s.warning, backgroundColor: tint(s.warning, 0.1) },
      ".cm-line:has(.cm-inline-diagnostic-error)": { backgroundColor: tint(s.error, dark ? 0.06 : 0.04) },
      ".cm-line:has(.cm-inline-diagnostic-warning)": { backgroundColor: tint(s.warning, dark ? 0.05 : 0.04) },

      // ───── Marcadores em comentários (TODO, FIXME…) ─────
      ".cm-marker": { fontWeight: "700", fontStyle: "normal", borderRadius: "3px", padding: "0 3px" },
      ".cm-marker-todo": { color: s.warning, backgroundColor: tint(s.warning, 0.14) },
      ".cm-marker-fixme, .cm-marker-bug": { color: s.error, backgroundColor: tint(s.error, 0.14) },
      ".cm-marker-hack, .cm-marker-xxx": { color: s.hint, backgroundColor: tint(s.hint, 0.14) },
      ".cm-marker-note": { color: s.info, backgroundColor: tint(s.info, 0.14) },
    },
    { dark },
  );
}

// ───────────── Sintaxe ─────────────

interface Syntax {
  keyword: string;
  fn: string;
  type: string;
  string: string;
  number: string;
  comment: string;
  property: string;
  /** Nome da tag (HTML/JSX/XML). */
  tag: string;
  /** Nome do atributo da tag. */
  attribute: string;
  /** < > </ /> */
  angle: string;
  operator: string;
  regexp: string;
  /** this/self e variáveis especiais. */
  self: string;
  /** Decorators, anotações, diretivas. */
  meta: string;
}

function highlightFor(c: Palette, s: Syntax, dark: boolean): Extension {
  return syntaxHighlighting(
    HighlightStyle.define([
      {
        tag: [
          t.keyword,
          t.controlKeyword,
          t.moduleKeyword,
          t.operatorKeyword,
          t.definitionKeyword,
          t.modifier,
        ],
        color: s.keyword,
      },
      { tag: [t.function(t.variableName), t.function(t.propertyName), t.macroName], color: s.fn },
      {
        tag: [t.className, t.typeName, t.definition(t.typeName), t.namespace, t.standard(t.typeName)],
        color: s.type,
      },
      { tag: [t.definition(t.variableName), t.definition(t.propertyName)], color: c.strong },
      { tag: [t.string, t.special(t.string), t.character, t.docString], color: s.string },
      { tag: [t.regexp, t.escape], color: s.regexp },
      { tag: [t.number, t.bool, t.null, t.atom, t.unit, t.color, t.integer, t.float], color: s.number },
      {
        tag: [t.comment, t.lineComment, t.blockComment, t.docComment],
        color: s.comment,
        fontStyle: "italic",
      },
      { tag: [t.propertyName], color: s.property },
      { tag: [t.self, t.special(t.variableName)], color: s.self, fontStyle: "italic" },
      { tag: [t.labelName], color: s.meta },
      // Tags e atributos: o que a pessoa mais procura em HTML/JSX.
      { tag: [t.tagName, t.standard(t.tagName)], color: s.tag, fontWeight: "600" },
      { tag: [t.attributeName], color: s.attribute },
      { tag: [t.attributeValue], color: s.string },
      { tag: [t.angleBracket], color: s.angle },
      { tag: [t.heading], color: s.fn, fontWeight: "700" },
      { tag: [t.emphasis], fontStyle: "italic" },
      { tag: [t.strong], fontWeight: "700" },
      { tag: [t.strikethrough], textDecoration: "line-through" },
      { tag: [t.link, t.url], color: s.type, textDecoration: "underline" },
      { tag: [t.punctuation, t.bracket, t.separator], color: c.muted },
      { tag: [t.operator, t.derefOperator], color: s.operator },
      { tag: [t.meta, t.annotation, t.processingInstruction, t.documentMeta], color: s.meta },
      { tag: [t.invalid], color: signalColors(dark).error },
    ]),
  );
}

/** Paleta padrão do Kompil: viva sobre o preto puro, contida no claro. */
function kompilSyntax(dark: boolean): Syntax {
  return dark
    ? {
        keyword: "#c4a7ff",
        fn: "#7cc4ff",
        type: "#5eead4",
        string: "#a6e3a1",
        number: "#fab387",
        comment: "#6b7083",
        property: "#d4d8e8",
        tag: "#ff7b9c",
        attribute: "#ffcb6b",
        angle: "#8b8fa3",
        operator: "#89ddff",
        regexp: "#f5a3d0",
        self: "#ff9cc1",
        meta: "#ffcb6b",
      }
    : {
        keyword: "#7c3aed",
        fn: "#0369a1",
        type: "#0f766e",
        string: "#15803d",
        number: "#c2410c",
        comment: "#8b8fa3",
        property: "#1f2937",
        tag: "#be123c",
        attribute: "#b45309",
        angle: "#6b7280",
        operator: "#0e7490",
        regexp: "#a21caf",
        self: "#be185d",
        meta: "#b45309",
      };
}

/** Monocromático (identidade original da seção 21): peso, itálico e tons de cinza. */
function monoHighlight(c: Palette): Extension {
  return syntaxHighlighting(
    HighlightStyle.define([
      {
        tag: [t.keyword, t.controlKeyword, t.moduleKeyword, t.operatorKeyword, t.definitionKeyword],
        color: c.strong,
        fontWeight: "700",
      },
      { tag: [t.function(t.variableName), t.function(t.propertyName)], color: c.strong },
      {
        tag: [t.definition(t.variableName), t.definition(t.propertyName), t.className, t.typeName],
        color: c.strong,
        fontWeight: "600",
      },
      { tag: [t.string, t.special(t.string), t.regexp, t.character, t.attributeValue], color: c.muted },
      { tag: [t.number, t.bool, t.null, t.atom], color: c.fg, fontWeight: "600" },
      {
        tag: [t.comment, t.lineComment, t.blockComment, t.docComment],
        color: c.dim,
        fontStyle: "italic",
      },
      { tag: [t.propertyName, t.attributeName], color: c.fg },
      { tag: [t.tagName], color: c.strong, fontWeight: "700" },
      { tag: [t.angleBracket], color: c.dim },
      { tag: [t.heading], color: c.strong, fontWeight: "700" },
      { tag: [t.emphasis], fontStyle: "italic" },
      { tag: [t.strong], fontWeight: "700" },
      { tag: [t.link, t.url], color: c.muted, textDecoration: "underline" },
      { tag: [t.punctuation, t.bracket, t.operator], color: c.muted },
      { tag: [t.meta, t.annotation], color: c.dim },
      { tag: [t.invalid], textDecoration: `underline wavy ${c.strong}` },
    ]),
  );
}

/**
 * Tema padrão do editor: preto e branco (identidade da seção 21). A sintaxe se distingue por
 * peso, itálico e tons de cinza; cor no código só com um tema instalado pela loja de plugins.
 */
export function editorTheme(dark: boolean): Extension {
  const c = palette(dark);
  return [viewTheme(c, dark, monoSignals(c)), monoHighlight(c)];
}

// ───────────── Temas coloridos (instalados como plugins) ─────────────

const COLOR_THEMES = {
  /** A sintaxe colorida do Kompil, sem mexer no resto (era o padrão antes dos temas). */
  kompil: {
    surface: (_dark: boolean) => ({}),
    syntax: kompilSyntax,
  },
  /** Cores frias sobre o preto. */
  aurora: {
    surface: (dark: boolean) =>
      dark ? { bg: "#020308", gutter: "#020308", line: "#0b0d17", sel: "#1c2240", selFocus: "#252c52" } : {},
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#b392f0",
            fn: "#79b8ff",
            type: "#73e0d0",
            string: "#a5e075",
            number: "#8be9fd",
            comment: "#5c6478",
            property: "#c8d3f5",
            tag: "#7aa2f7",
            attribute: "#bb9af7",
            angle: "#5c6a9a",
            operator: "#89ddff",
            regexp: "#b4f9f8",
            self: "#f7768e",
            meta: "#e0af68",
          }
        : {
            keyword: "#7c3aed",
            fn: "#1d4ed8",
            type: "#0f766e",
            string: "#3f7f16",
            number: "#0369a1",
            comment: "#8a93a6",
            property: "#1e293b",
            tag: "#2563eb",
            attribute: "#7c3aed",
            angle: "#64748b",
            operator: "#0e7490",
            regexp: "#0891b2",
            self: "#be123c",
            meta: "#a16207",
          },
  },
  /** Tons quentes, alto contraste. */
  ember: {
    surface: (dark: boolean) =>
      dark ? { bg: "#0d0b0a", gutter: "#0d0b0a", line: "#191512", sel: "#3a2a1f", selFocus: "#4a3424" } : {},
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#ff7a59",
            fn: "#ffb454",
            type: "#f29e74",
            string: "#e9d38a",
            number: "#ff6b9a",
            comment: "#6f655c",
            property: "#ece0cf",
            tag: "#ff8f40",
            attribute: "#ffd580",
            angle: "#8a7a6a",
            operator: "#f29668",
            regexp: "#95e6cb",
            self: "#ff6b9a",
            meta: "#ffd580",
          }
        : {
            keyword: "#c2410c",
            fn: "#b45309",
            type: "#9a3412",
            string: "#7c5e10",
            number: "#be185d",
            comment: "#a8998a",
            property: "#292524",
            tag: "#c2410c",
            attribute: "#a16207",
            angle: "#78716c",
            operator: "#9a3412",
            regexp: "#0f766e",
            self: "#be185d",
            meta: "#a16207",
          },
  },
  /** Dracula (escuro) e Alucard (claro). */
  dracula: {
    surface: (dark: boolean) =>
      dark
        ? {
            bg: "#282a36",
            gutter: "#282a36",
            fg: "#f8f8f2",
            line: "#2f3241",
            sel: "#44475a",
            selFocus: "#4f5370",
          }
        : { bg: "#fffbeb", gutter: "#fffbeb", line: "#f6f1dc", sel: "#e8e1c5", selFocus: "#ded5b2" },
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#ff79c6",
            fn: "#50fa7b",
            type: "#8be9fd",
            string: "#f1fa8c",
            number: "#bd93f9",
            comment: "#6272a4",
            property: "#f8f8f2",
            tag: "#ff79c6",
            attribute: "#50fa7b",
            angle: "#6272a4",
            operator: "#ff79c6",
            regexp: "#ff5555",
            self: "#bd93f9",
            meta: "#ffb86c",
          }
        : {
            keyword: "#a3144d",
            fn: "#14710a",
            type: "#036a96",
            string: "#846e15",
            number: "#644ac9",
            comment: "#635d97",
            property: "#1f1f1f",
            tag: "#a3144d",
            attribute: "#14710a",
            angle: "#635d97",
            operator: "#a3144d",
            regexp: "#cb3a2a",
            self: "#644ac9",
            meta: "#a34d14",
          },
  },
  /** One Dark (Atom) e One Light. */
  "one-dark": {
    surface: (dark: boolean) =>
      dark
        ? {
            bg: "#282c34",
            gutter: "#282c34",
            fg: "#abb2bf",
            line: "#2c313c",
            sel: "#3e4451",
            selFocus: "#4b5263",
          }
        : {
            bg: "#fafafa",
            gutter: "#fafafa",
            fg: "#383a42",
            line: "#f0f0f0",
            sel: "#e5e5e6",
            selFocus: "#d9d9da",
          },
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#c678dd",
            fn: "#61afef",
            type: "#e5c07b",
            string: "#98c379",
            number: "#d19a66",
            comment: "#7f848e",
            property: "#e06c75",
            tag: "#e06c75",
            attribute: "#d19a66",
            angle: "#abb2bf",
            operator: "#56b6c2",
            regexp: "#56b6c2",
            self: "#e06c75",
            meta: "#d19a66",
          }
        : {
            keyword: "#a626a4",
            fn: "#4078f2",
            type: "#c18401",
            string: "#50a14f",
            number: "#986801",
            comment: "#a0a1a7",
            property: "#e45649",
            tag: "#e45649",
            attribute: "#986801",
            angle: "#383a42",
            operator: "#0184bc",
            regexp: "#0184bc",
            self: "#e45649",
            meta: "#986801",
          },
  },
  /** Cores do GitHub (dark default e light default). */
  github: {
    surface: (dark: boolean) =>
      dark
        ? {
            bg: "#0d1117",
            gutter: "#0d1117",
            fg: "#e6edf3",
            line: "#161b22",
            sel: "#264f78",
            selFocus: "#2f5d8a",
          }
        : {
            bg: "#ffffff",
            gutter: "#ffffff",
            fg: "#1f2328",
            line: "#f6f8fa",
            sel: "#d1e4f9",
            selFocus: "#bcd7f5",
          },
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#ff7b72",
            fn: "#d2a8ff",
            type: "#ffa657",
            string: "#a5d6ff",
            number: "#79c0ff",
            comment: "#8b949e",
            property: "#79c0ff",
            tag: "#7ee787",
            attribute: "#79c0ff",
            angle: "#8b949e",
            operator: "#ff7b72",
            regexp: "#7ee787",
            self: "#79c0ff",
            meta: "#d2a8ff",
          }
        : {
            keyword: "#cf222e",
            fn: "#8250df",
            type: "#953800",
            string: "#0a3069",
            number: "#0550ae",
            comment: "#6e7781",
            property: "#0550ae",
            tag: "#116329",
            attribute: "#0550ae",
            angle: "#6e7781",
            operator: "#cf222e",
            regexp: "#116329",
            self: "#0550ae",
            meta: "#8250df",
          },
  },
  /** Nord: tons árticos. */
  nord: {
    surface: (dark: boolean) =>
      dark
        ? {
            bg: "#2e3440",
            gutter: "#2e3440",
            fg: "#d8dee9",
            line: "#3b4252",
            sel: "#434c5e",
            selFocus: "#4c566a",
          }
        : {
            bg: "#eceff4",
            gutter: "#eceff4",
            fg: "#2e3440",
            line: "#e5e9f0",
            sel: "#d8dee9",
            selFocus: "#cbd3e0",
          },
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#81a1c1",
            fn: "#88c0d0",
            type: "#8fbcbb",
            string: "#a3be8c",
            number: "#b48ead",
            comment: "#616e88",
            property: "#d8dee9",
            tag: "#81a1c1",
            attribute: "#8fbcbb",
            angle: "#616e88",
            operator: "#81a1c1",
            regexp: "#ebcb8b",
            self: "#81a1c1",
            meta: "#d08770",
          }
        : {
            keyword: "#5e81ac",
            fn: "#2e6b7a",
            type: "#2f7a77",
            string: "#4f7a37",
            number: "#8b5a85",
            comment: "#7b8394",
            property: "#2e3440",
            tag: "#5e81ac",
            attribute: "#2f7a77",
            angle: "#7b8394",
            operator: "#5e81ac",
            regexp: "#9a7a1f",
            self: "#5e81ac",
            meta: "#b0563b",
          },
  },
  /** Monokai: alto contraste do Sublime Text. */
  monokai: {
    surface: (dark: boolean) =>
      dark
        ? {
            bg: "#272822",
            gutter: "#272822",
            fg: "#f8f8f2",
            line: "#3e3d32",
            sel: "#49483e",
            selFocus: "#5a594d",
          }
        : {
            bg: "#fafaf5",
            gutter: "#fafaf5",
            fg: "#272822",
            line: "#f0efe6",
            sel: "#e3e2d6",
            selFocus: "#d6d5c6",
          },
    syntax: (dark: boolean): Syntax =>
      dark
        ? {
            keyword: "#f92672",
            fn: "#a6e22e",
            type: "#66d9ef",
            string: "#e6db74",
            number: "#ae81ff",
            comment: "#75715e",
            property: "#f8f8f2",
            tag: "#f92672",
            attribute: "#a6e22e",
            angle: "#75715e",
            operator: "#f92672",
            regexp: "#e6db74",
            self: "#fd971f",
            meta: "#fd971f",
          }
        : {
            keyword: "#d6195b",
            fn: "#5d8a00",
            type: "#1a8bb0",
            string: "#a08a00",
            number: "#7a4fcf",
            comment: "#8a876f",
            property: "#272822",
            tag: "#d6195b",
            attribute: "#5d8a00",
            angle: "#8a876f",
            operator: "#d6195b",
            regexp: "#a08a00",
            self: "#c46a00",
            meta: "#c46a00",
          },
  },
} as const;

export type ColorThemeId = keyof typeof COLOR_THEMES;

/**
 * Tema Hacker (plugin): só o CÓDIGO muda — tudo em verde, com palavras-chave, funções, tipos e
 * tags em verde negrito. Fundo, margens, cursor e o resto da IDE continuam como estão.
 */
export function hackerTheme(dark: boolean): Extension {
  const c = palette(dark);
  const g = dark
    ? {
        text: "#33ff66",
        strong: "#00ff41",
        fn: "#7dffa0",
        type: "#39d353",
        string: "#b6ffb0",
        number: "#5af78e",
        comment: "#1f9a45",
        punct: "#22a34a",
        operator: "#00cc33",
      }
    : {
        text: "#0a5c25",
        strong: "#007a29",
        fn: "#046b2a",
        type: "#0f8a3a",
        string: "#2f7a1f",
        number: "#00702a",
        comment: "#5f9c70",
        punct: "#3d8a52",
        operator: "#0a6e2c",
      };
  return [
    viewTheme(c, dark, monoSignals(c)),
    EditorView.theme({ ".cm-content": { color: g.text } }, { dark }),
    syntaxHighlighting(
      HighlightStyle.define([
        {
          tag: [
            t.keyword,
            t.controlKeyword,
            t.moduleKeyword,
            t.operatorKeyword,
            t.definitionKeyword,
            t.modifier,
          ],
          color: g.strong,
          fontWeight: "700",
        },
        {
          tag: [t.function(t.variableName), t.function(t.propertyName), t.macroName],
          color: g.fn,
          fontWeight: "700",
        },
        {
          tag: [t.className, t.typeName, t.definition(t.typeName), t.namespace, t.standard(t.typeName)],
          color: g.type,
          fontWeight: "700",
        },
        {
          tag: [t.definition(t.variableName), t.definition(t.propertyName)],
          color: g.strong,
          fontWeight: "600",
        },
        { tag: [t.string, t.special(t.string), t.character, t.docString, t.attributeValue], color: g.string },
        { tag: [t.regexp, t.escape], color: g.string, fontWeight: "600" },
        { tag: [t.number, t.bool, t.null, t.atom, t.unit, t.color, t.integer, t.float], color: g.number },
        {
          tag: [t.comment, t.lineComment, t.blockComment, t.docComment],
          color: g.comment,
          fontStyle: "italic",
        },
        { tag: [t.propertyName, t.attributeName], color: g.fn },
        { tag: [t.self, t.special(t.variableName)], color: g.strong, fontStyle: "italic" },
        { tag: [t.tagName, t.standard(t.tagName)], color: g.strong, fontWeight: "700" },
        { tag: [t.angleBracket, t.punctuation, t.bracket, t.separator], color: g.punct },
        { tag: [t.operator, t.derefOperator], color: g.operator },
        { tag: [t.heading], color: g.strong, fontWeight: "700" },
        { tag: [t.strong], fontWeight: "700" },
        { tag: [t.emphasis], fontStyle: "italic" },
        { tag: [t.link, t.url], color: g.fn, textDecoration: "underline" },
        { tag: [t.meta, t.annotation, t.processingInstruction, t.documentMeta, t.labelName], color: g.type },
        { tag: [t.invalid], textDecoration: `underline wavy ${c.strong}` },
      ]),
    ),
  ];
}

/** Tema convertido do VS Code (mesma paleta e categorias de realce do Kompil). */
export function importedEditorTheme(t: { dark: boolean; editor: Palette; syntax: Syntax }): Extension {
  return [viewTheme(t.editor, t.dark, signalColors(t.dark)), highlightFor(t.editor, t.syntax, t.dark)];
}

export function colorTheme(id: ColorThemeId, dark: boolean): Extension {
  const def = COLOR_THEMES[id];
  const c: Palette = { ...palette(dark), ...def.surface(dark) };
  return [viewTheme(c, dark, signalColors(dark)), highlightFor(c, def.syntax(dark), dark)];
}
